'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import {
  ParagraphCollectionFragment,
  SingleLineFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { ParagraphCollectionRenderer } from '@/ui/components/tileRenderers/ParagraphCollectionRenderer/ParagraphCollectionRenderer';
import Spinner from '@/ui/components/shared/Spinner/Spinner';
import Button from '@/ui/components/shared/Button';
import LogoBar from '@/ui/components/pages/activation-code-view/app-store-list/app-store-list';
import { TrackingConversionType } from '@/utils/tracking-conversion-types';
import { trackConversion } from '@/utils/tracking-helper';
import { activateRmaTrial } from './rma-trial-thank-you-actions';
import { RMA_ACTIVATION_RESULT, RmaActivationResult } from './rma-trial-thank-you-types';

export type RmaTrialThankYouTile = {
  content: ParagraphCollectionFragment;
  'activation-failure-content': ParagraphCollectionFragment;
  'remote-access-already-active-content': ParagraphCollectionFragment;
  'cta-text'?: SingleLineFragment;
  'cta-url'?: SingleLineFragment;
};

interface RmaTrialThankYouProps {
  tile: RmaTrialThankYouTile;
}

export function RmaTrialThankYou({ tile }: Readonly<RmaTrialThankYouProps>) {
  const t = useTranslations('RmaTrialThankYou');
  const ctaText = tile['cta-text']?.text ?? t('ctaText');
  const ctaUrl = tile['cta-url']?.text ?? t('ctaUrl');
  const searchParams = useSearchParams();
  const rmaActivationUuid = searchParams.get('uuid') ?? '';

  const [activationResult, setActivationResult] = useState<RmaActivationResult | null>(null);
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (hasFiredRef.current) {
      return;
    }

    hasFiredRef.current = true;

    if (!rmaActivationUuid) {
      setActivationResult(RMA_ACTIVATION_RESULT.FAILURE);
      return;
    }

    const runActivation = async () => {
      const result = await activateRmaTrial(rmaActivationUuid);
      setActivationResult(result);
    };

    runActivation();
  }, [rmaActivationUuid]);

  useEffect(() => {
    if (activationResult !== RMA_ACTIVATION_RESULT.SUCCESS) {
      return;
    }

    const ndt = searchParams.get('ndt') ?? '';
    const nukiIdDecimal = searchParams.get('nukiId') ?? '';

    trackConversion(TrackingConversionType.SERVICE_ACTIVATED, {
      uuid: rmaActivationUuid,
      ndt,
      nukiId: Number(nukiIdDecimal).toString(16).toUpperCase(),
    });
  }, [activationResult, rmaActivationUuid, searchParams]);

  if (activationResult === null) {
    return (
      <output
        className="mx-auto flex max-w-2xl flex-col items-center gap-4 text-center sm:my-14 sm:px-8 lg:my-16"
        aria-live="polite"
      >
        <Spinner className="h-10 w-10" />
        <p className="text-lg">{t('loading')}</p>
      </output>
    );
  }

  let content: ParagraphCollectionFragment;

  const showCta =
    activationResult === RMA_ACTIVATION_RESULT.SUCCESS ||
    activationResult === RMA_ACTIVATION_RESULT.ALREADY_ACTIVE;

  if (activationResult === RMA_ACTIVATION_RESULT.SUCCESS) {
    content = tile.content;
  } else if (activationResult === RMA_ACTIVATION_RESULT.ALREADY_ACTIVE) {
    content = tile['remote-access-already-active-content'];
  } else {
    content = tile['activation-failure-content'];
  }

  return (
    <div className="mx-auto my-14 max-w-3xl text-center sm:px-8 lg:my-16">
      <ParagraphCollectionRenderer
        content={content}
        titleClassName="text-3xl leading-tight mb-5 max-w-xl mx-auto"
        bodyClassName="text-2xl max-w-3xl mx-auto"
        highlightTitlePunctuation
        removeParagraphMargins
      />

      {showCta && (
        <div className="mt-8 flex flex-col items-center gap-6">
          {ctaUrl && ctaText && (
            <Button href={ctaUrl} openInNewTab>
              {ctaText}
            </Button>
          )}

          <div
            className="flex w-full max-w-md items-center gap-4 px-4 text-sm uppercase text-neutral-60"
            aria-label={t('orDivider')}
          >
            <hr className="m-0 h-px flex-1 border-0 bg-neutral-20" />
            <span className="font-medium">{t('orDivider')}</span>
            <hr className="m-0 h-px flex-1 border-0 bg-neutral-20" />
          </div>

          <LogoBar userAgent={typeof navigator === 'undefined' ? 'other' : navigator.userAgent} />
        </div>
      )}
    </div>
  );
}
